import React, { useCallback, useEffect, useState } from 'react';
import * as yup from 'yup';
import { CircularProgress } from '@material-ui/core';
import { FormSpy } from 'react-final-form';
import { FORM_ERROR, FormState, FormApi } from 'final-form';
import { useHistory } from 'react-router-dom';
import { OTPConfirmationModal, StatusModal, WarningModal } from 'components/modals';
import Button from 'ui/Button/Button';
import Form from 'ui/Form/Form';
import FieldsLeveler from 'ui/structure/FieldsLeveler/FieldsLeveler';
import SubmitLeveler from 'ui/structure/SubmitLeveler/SubmitLeveler';
import { requiredOnly, validateAmount, validationCallback } from 'utils/validation';
import { AxiosStatus } from 'api/axiosConfig';
import { changeFieldValue, setFieldTouched } from 'utils/formMutators';
import { IAccount } from 'store/accounts.model';
import { IOTPError } from 'components/authModules/LogIn/login.model';
import api from 'api/api';
import { NETWORK_REQUIRED } from 'utils/constants';
import routePaths from 'routes/routePaths';
import { getTransactionsArray } from 'utils/mapping/transactionCardMapper';
import { IApiResponse, Signatory, UserRoles } from 'utils/models';
import auth from 'utils/auth';
import { renderSingleCardLoadFields } from './SingleCardLoadFields';
import { ICardsState, IFormValues, IHandleFieldsChange, ITransactionField, sortArrays } from './SingleCardLoad.model';
import { ON_SUBMITS, validatePrepardCard } from './submitApi';

const schema = yup.object().shape({
  accountNumber: requiredOnly('Source Account'),
  amount: validateAmount(),
  narration: requiredOnly('Narration'),
  customerId: requiredOnly('Customer ID'),
  last4Digit: requiredOnly('Card last four digit'),
  cardName: requiredOnly('Card Name'),
  phoneNumber: requiredOnly('PhoneNumber'),
});

interface IProps {
  accounts: IAccount[];
  handleChangeForm: () => void;
}

const SingleCardLoad = ({ handleChangeForm, accounts }: IProps) => {
  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const showStatusModal = submitStatus === AxiosStatus.SUCCESS || submitStatus === AxiosStatus.FAILURE;
  const submitting = submitStatus === AxiosStatus.PENDING;
  const [showValidationWarning, setShowValidationWarning] = useState(false);
  const [beneficiaryData, setBeneficiaryData] = useState<Record<string, string>>({});

  const [isConfirmationOpen, setConfirmationOpen] = useState(false);
  const [previewData, setPreviewData] = useState<ITransactionField[]>([]);
  const [OTPError, setOTPError] = useState<IOTPError | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [successMsg, setSuccessMsg] = useState<string>('');
  const [state, setState] = useState<ICardsState>({
    beneficiaries: [],
  });
  let mainForm!: FormApi<IFormValues, Partial<IFormValues>>;

  const validate = useCallback((values: IFormValues) => validationCallback<IFormValues>(schema)(values), []);
  const updateState = useCallback(
    (update: Partial<ICardsState>) => setState((current) => ({ ...current, ...update })),
    [],
  );

  useEffect(() => {
    const fetchBeneficiaries = () =>
      api
        .get(`beneficiary/prepaidcard?type=&loginId=${auth.data?.loginId ?? ''}`)
        .then((data: ICardsState) => updateState({ beneficiaries: data.beneficiaries }))
        .catch((e) => console.error(e));

    Promise.all([fetchBeneficiaries()]).finally(() => {
      console.log('readyValues');
    });
  }, []);

  const history = useHistory();
  const handleOnSuccess = () => {
    history.push(routePaths.prepaidCardLoad);
  };

  const closeConfirmation = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    } else {
      setConfirmationOpen(false);
    }
  };
  const onCloseStatusModal = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    }
    setSubmitStatus(AxiosStatus.DEFAULT);
  };

  const openConfirmation = () => {
    setShowValidationWarning(false);
    setConfirmationOpen(true);
  };

  const onRejectedSubmit = useCallback((error: IApiResponse) => {
    console.error(error || NETWORK_REQUIRED);
    setErrorMsg(error.description || NETWORK_REQUIRED);
    setSubmitStatus(AxiosStatus.FAILURE);
    closeConfirmation();
  }, []);

  const handleFieldsChange: IHandleFieldsChange = () => {
    if (mainForm.getState().values.customerId) {
      const requestBody = mainForm.getState().values.customerId;

      setSubmitStatus(AxiosStatus.PENDING);

      validatePrepardCard(requestBody)
        .then((response) => {
          setBeneficiaryData({
            name: response.customerResponse?.preferredNameField,
            phoneNumber: response.customerResponse?.mobilePhoneNumberField,
          });
          const { preferredNameField, mobilePhoneNumberField } = response.customerResponse;
          mainForm.change('cardName', preferredNameField);
          mainForm.change('phoneNumber', mobilePhoneNumberField);

          setSubmitStatus(AxiosStatus.DEFAULT);
        })
        .catch((error) => {
          setErrorMsg(error.description || 'Could not validate your card.');
          setShowValidationWarning(true);
        })
        .finally(() => setSubmitStatus(AxiosStatus.DEFAULT));
    }
  };

  const onSubmit = (values: IFormValues) => {
    const account = accounts.find((a) => a.accountNumber === values.accountNumber);

    if (Number(account?.availableBalance) < Number(values?.amount)) {
      setSubmitStatus(AxiosStatus.FAILURE);
      setErrorMsg('Transfer failed.\nInsufficient funds to complete the transaction.');
      return;
    }
    const readyValues = {
      ...values,
    };
    setPreviewData(getTransactionsArray(readyValues, undefined, undefined, sortArrays));
    setSubmitStatus(AxiosStatus.PENDING);
    api
      .post('/customer/otp/generate')
      .then(() => {
        setSubmitStatus(AxiosStatus.DEFAULT);
        openConfirmation();
      })
      .catch((data) => {
        onRejectedSubmit(data);
      });
  };

  const closeValidationWarning = () => {
    setShowValidationWarning(false);
    setErrorMsg('');
  };

  const onChangeForm = useCallback(
    ({ dirty }: FormState<IFormValues>) => {
      if (dirty) {
        handleChangeForm();
      }
    },
    [handleChangeForm],
  );

  const onResend = () => api.post('/customer/otp/generate');
  const onRetryConfirm = () => setSubmitStatus(AxiosStatus.DEFAULT);

  const getMessage = (role: UserRoles) => {
    switch (role) {
      case Signatory.INITIATOR:
        return 'Transfer request has been successfully sent for approval!';
      default:
        return 'Transfer Completed Successfully!';
    }
  };

  const onConfirm = useCallback(
    (values: IFormValues, form) => (otp: string, saveBeneficiary: boolean) => {
      setErrorMsg('');
      setSubmitStatus(AxiosStatus.PENDING);
      api
        .post(`customer/otp/verify/${otp || ''}/false/${String(sessionStorage.getItem('userName'))}`)
        .then(() => {
          setSubmitStatus(AxiosStatus.PENDING);
          setSuccessMsg(getMessage('Initiator'));

          return ON_SUBMITS({ setSubmitStatus, setSuccessMsg, setErrorMsg })(
            values,
            form,
            otp,
            state,
            beneficiaryData,
            saveBeneficiary,
          );
        })
        .catch((error: IOTPError) => {
          setOTPError(error);
          if (error.failureCount === 0) {
            return { [FORM_ERROR]: error.description || NETWORK_REQUIRED };
          }
          return null;
        });
    },
    [],
  );

  return (
    <Form
      onSubmit={onSubmit}
      validate={validate}
      mutators={{ changeFieldValue, setFieldTouched }}
      render={({ handleSubmit, form, values, hasValidationErrors }) => {
        mainForm = mainForm || form;
        return (
          <form onSubmit={handleSubmit}>
            <FieldsLeveler label="Fund Prepaid Card Details">
              {renderSingleCardLoadFields(values, state, accounts, form, submitting, handleFieldsChange)}
            </FieldsLeveler>
            <SubmitLeveler>
              <Button type="submit" fullWidth disabled={submitting || hasValidationErrors}>
                {submitting ? <CircularProgress size={20} color="inherit" /> : 'Continue'}
              </Button>
            </SubmitLeveler>
            <StatusModal
              open={showStatusModal}
              status={submitStatus === AxiosStatus.SUCCESS ? 'success' : 'failure'}
              onClose={onCloseStatusModal}
              onRetry={onCloseStatusModal}
              onRetryText="Close"
            >
              {submitStatus === AxiosStatus.SUCCESS ? successMsg : (
                <div style={{ whiteSpace: 'pre-line' }}>{errorMsg}</div>
              )}
            </StatusModal>
            <WarningModal open={showValidationWarning} cancelName="Close" onClose={closeValidationWarning}>
              {errorMsg}
            </WarningModal>
            <OTPConfirmationModal
              open={isConfirmationOpen}
              data={previewData}
              onClose={closeConfirmation}
              onResend={onResend}
              OTPPinError={OTPError}
              onSubmit={onConfirm(values, form)}
              submitStatus={submitStatus}
              onSuccess={handleOnSuccess}
              onRetry={onRetryConfirm}
              successText={successMsg}
              failureText={errorMsg}
              askSaveBeneficiary
            />
            <FormSpy subscription={{ values: true, dirty: true }} onChange={onChangeForm} />
          </form>
        );
      }}
    />
  );
};

export default SingleCardLoad;
